import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCopyPlanItem, useDeletePlanItem } from '@/hooks/usePlanDetail'
import { usePatchDagRows, useBulkColumnEdit } from '@/hooks/useDagTable'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { updateDagRowInCache } from '@/lib/dagCacheUtils'
import { queryKeys } from '@/lib/queryKeys'
import type { SkuSaveData } from './SkuSearchCell'
import type {
  DagColumnMeta,
  BatchPatchResponse,
  DagRowRead,
  RowPatchResult,
  BulkColumnEditRequest,
  BulkColumnEditResponse,
} from '@/types/dag'

interface MutationCallbacks {
  onEditSuccess?: () => void
  onRowRefreshNeeded?: (rowId: string) => void
  onRowsPatched?: (patches: BatchPatchResponse['patches']) => void
  onCopySuccess?: () => void
  onDeleteSuccess?: () => void
}

interface PendingPatch {
  rowId: string
  rowVersion: number
  cells: Array<{ columnId: string; value: unknown }>
}

export function useProductsTableMutations(
  planId: string,
  callbacks: MutationCallbacks = {},
  options?: {
    versionId?: string
    columnsMap?: Map<string, DagColumnMeta>
    userId?: string
  }
) {
  const queryClient = useQueryClient()
  const copyItemMutation = useCopyPlanItem(planId)
  const deleteItemMutation = useDeletePlanItem(planId)
  const patchDagRowsMutation = usePatchDagRows(planId, options?.versionId)
  const bulkColumnEditMutation = useBulkColumnEdit(planId, options?.versionId)

  const getComputingColumns = useCallback(
    (cells: Array<{ columnId: string; value: unknown }>) => {
      const computingIds = new Set<string>()

      for (const cell of cells) {
        computingIds.add(cell.columnId)
        const meta = options?.columnsMap?.get(cell.columnId)
        if (meta?.triggers_dag) {
          for (const dep of meta.dependents) {
            computingIds.add(dep)
          }
        }
      }

      return Array.from(computingIds)
    },
    [options?.columnsMap]
  )

  const handlePatchSuccess = useCallback(
    (data: BatchPatchResponse) => {
      const versionId = options?.versionId

      if (versionId) {
        for (const patch of data.patches) {
          const updatedCells: Record<string, unknown> = {}
          for (const cell of patch.cells) {
            updatedCells[cell.column_id] = cell.value
          }
          updateDagRowInCache(
            queryClient,
            planId,
            versionId,
            patch.row_id,
            updatedCells,
            patch.new_version
          )
        }

        requestAnimationFrame(() => {
          for (const patch of data.patches) {
            useDagTableStore.getState().clearAllComputing(patch.row_id)
          }
        })
      }

      if (data.aggregates) {
        useDagTableStore.getState().setAggregates(data.aggregates)
      }

      callbacks.onRowsPatched?.(data.patches)
      callbacks.onEditSuccess?.()

      for (const patch of data.patches) {
        callbacks.onRowRefreshNeeded?.(patch.row_id)
      }
    },
    [callbacks, options?.versionId, planId, queryClient]
  )

  const executeBatchPatch = useCallback(
    (pendingPatches: PendingPatch[], errorPrefix: string) => {
      const computingByRow = new Map<string, string[]>()

      for (const patch of pendingPatches) {
        const computingIds = getComputingColumns(patch.cells)
        computingByRow.set(patch.rowId, computingIds)
        useDagTableStore.getState().setComputing(patch.rowId, computingIds)
      }

      patchDagRowsMutation.mutate(
        {
          patches: pendingPatches.map(patch => ({
            row_id: patch.rowId,
            cells: patch.cells.map(cell => ({
              column_id: cell.columnId,
              value: cell.value,
            })),
            expected_version: patch.rowVersion,
          })),
          user_id: options?.userId ?? 'current-user',
        },
        {
          onSuccess: (data: BatchPatchResponse) => {
            handlePatchSuccess(data)

            const shouldInvalidateDropdowns = pendingPatches.some(patch =>
              patch.cells.some(
                cell =>
                  options?.columnsMap?.get(cell.columnId)?.dropdown_mode ===
                  'EXTENSIBLE'
              )
            )

            if (shouldInvalidateDropdowns) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.dropdowns.all,
              })
              // Columns whose dropdown_category doesn't match a global
              // lookup category (e.g. custom_attr:material) get their
              // options embedded directly in the dag-metadata response,
              // so a newly saved "Other..." value only shows up once
              // that query is refetched too.
              if (options?.versionId) {
                queryClient.invalidateQueries({
                  queryKey: queryKeys.dagTable.metadata(
                    planId,
                    options.versionId
                  ),
                })
              }
            }
          },
          onError: (error: Error) => {
            for (const [rowId, computingIds] of computingByRow.entries()) {
              for (const columnId of computingIds) {
                useDagTableStore.getState().clearComputing(rowId, columnId)
              }
            }

            toast.error(getErrorMessage(error, errorPrefix))
          },
        }
      )
    },
    [
      getComputingColumns,
      handlePatchSuccess,
      options?.columnsMap,
      options?.userId,
      options?.versionId,
      patchDagRowsMutation,
      planId,
      queryClient,
    ]
  )

  /**
   * DAG cell edit — patches a single cell via the new table/rows PATCH endpoint.
   * If the column triggers_dag and has dependents, immediately marks them as computing.
   */
  const handleCellEdit = useCallback(
    (rowId: string, columnId: string, value: unknown, rowVersion: number) => {
      executeBatchPatch(
        [
          {
            rowId,
            rowVersion,
            cells: [{ columnId, value }],
          },
        ],
        'Failed to update cell'
      )
    },
    [executeBatchPatch]
  )

  const handleDescriptionSave = useCallback(
    (rowId: string, value: string, rowVersion: number) => {
      handleCellEdit(rowId, 'description', value, rowVersion)
    },
    [handleCellEdit]
  )

  const handleSkuSave = useCallback(
    (itemId: string, data: SkuSaveData, rowVersion: number) => {
      executeBatchPatch(
        [
          {
            rowId: itemId,
            rowVersion,
            cells: [{ columnId: 'plan_sku_num', value: data.skuNumber }],
          },
        ],
        'Failed to update SKU'
      )
    },
    [executeBatchPatch]
  )

  /**
   * Multi-cell edit — patches multiple cells in a single row via the PATCH endpoint.
   * Used by hierarchy cascading dropdowns (department → class → subclass).
   */
  const handleMultiCellEdit = useCallback(
    (
      rowId: string,
      cells: Array<{ columnId: string; value: unknown }>,
      rowVersion: number
    ) => {
      executeBatchPatch(
        [
          {
            rowId,
            rowVersion,
            cells,
          },
        ],
        'Failed to update cells'
      )
    },
    [executeBatchPatch]
  )

  const handleBulkCellEdit = useCallback(
    (selectedRows: DagRowRead[], columnId: string, value: unknown) => {
      executeBatchPatch(
        selectedRows.map(row => ({
          rowId: row.id,
          rowVersion: row.row_version,
          cells: [{ columnId, value }],
        })),
        'Failed to update selected rows'
      )
    },
    [executeBatchPatch]
  )

  const handleBulkMultiCellEdit = useCallback(
    (
      selectedRows: DagRowRead[],
      cells: Array<{ columnId: string; value: unknown }>
    ) => {
      executeBatchPatch(
        selectedRows.map(row => ({
          rowId: row.id,
          rowVersion: row.row_version,
          cells,
        })),
        'Failed to update selected rows'
      )
    },
    [executeBatchPatch]
  )

  /**
   * Bulk column edit — applies a set/percent/flat arithmetic operation to one
   * column across many selected rows atomically, via the dedicated
   * bulk-column-edit endpoint (distinct from the cell-by-cell PATCH flow
   * above). Reuses handlePatchSuccess by mapping the response's per-row
   * old/new values into the same RowPatchResult[] shape the PATCH endpoint
   * returns, so cache updates / computing-clear / aggregates / selection
   * cache sync all come for free.
   */
  const handleBulkColumnEdit = useCallback(
    (
      request: BulkColumnEditRequest,
      onSuccess: (data: BulkColumnEditResponse) => void,
      onError: (error: Error) => void
    ) => {
      const computingIds = getComputingColumns([
        { columnId: request.column_id, value: request.value },
      ])
      for (const row of request.rows ?? []) {
        useDagTableStore.getState().setComputing(row.row_id, computingIds)
      }

      bulkColumnEditMutation.mutate(request, {
        onSuccess: (data: BulkColumnEditResponse) => {
          const patches: RowPatchResult[] = data.results.map(r => ({
            row_id: r.row_id,
            cells: [{ column_id: request.column_id, value: r.new_value }],
            new_version: r.new_version,
          }))
          handlePatchSuccess({ patches, aggregates: data.aggregates })

          if (
            options?.columnsMap?.get(request.column_id)?.dropdown_mode ===
            'EXTENSIBLE'
          ) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.dropdowns.all,
            })
            if (options?.versionId) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.dagTable.metadata(
                  planId,
                  options.versionId
                ),
              })
            }
          }

          onSuccess(data)
        },
        onError: (error: Error) => {
          for (const row of request.rows ?? []) {
            for (const columnId of computingIds) {
              useDagTableStore.getState().clearComputing(row.row_id, columnId)
            }
          }
          onError(error)
        },
      })
    },
    [
      bulkColumnEditMutation,
      getComputingColumns,
      handlePatchSuccess,
      options?.columnsMap,
      options?.versionId,
      planId,
      queryClient,
    ]
  )

  const handleConfirmCopy = useCallback(
    (itemId: string) => {
      copyItemMutation.mutate(itemId, {
        onSuccess: () => {
          toast.success('Line copied successfully')
          callbacks.onCopySuccess?.()
        },
        onError: (error: Error) => {
          toast.error(`Failed to copy: ${error.message}`)
        },
      })
    },
    [copyItemMutation, callbacks]
  )

  const handleConfirmDelete = useCallback(
    (itemId: string) => {
      deleteItemMutation.mutate(itemId, {
        onSuccess: () => {
          toast.success('Line deleted successfully')
          callbacks.onDeleteSuccess?.()
        },
        onError: (error: Error) => {
          toast.error(`Failed to delete: ${error.message}`)
        },
      })
    },
    [deleteItemMutation, callbacks]
  )

  return {
    // Mutations
    copyItemMutation,
    deleteItemMutation,
    patchDagRowsMutation,
    bulkColumnEditMutation,

    // Handlers
    handleCellEdit,
    handleMultiCellEdit,
    handleBulkCellEdit,
    handleBulkMultiCellEdit,
    handleBulkColumnEdit,
    handleDescriptionSave,
    handleSkuSave,
    handleConfirmCopy,
    handleConfirmDelete,
  }
}
